"use client";

// ISSUE-005 (QA 2026-05-02): a signed-in user navigating to "/" used to
// see the marketing landing page (because route-classes.ts classifies
// "/" as MARKETING). They could click around and look signed-out for
// a moment despite a live session. Now we redirect to /dashboard once
// auth has booted.
//
// Server-side redirect isn't an option — Amplify SSR auth crashes per
// the project memory note. So this is a client-side redirect that
// fires after `useAuth` has settled.
//
// Renders nothing. Kept as a sibling component (not wrapping children)
// so the marketing page's server component stays a server component
// and SEO metadata still flows.

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/hooks/useAuth";

export function LandingAuthRedirect() {
  const router = useRouter();
  const { user, isLoading, needsOnboarding } = useAuth();

  useEffect(() => {
    if (isLoading) return;
    if (!user) return;
    // Onboarding takes priority — let AuthContext's own redirect handle it.
    if (needsOnboarding) return;
    // Dev convenience: `/?preview` lets you view the marketing landing while
    // auto-logged-in locally (DEV_USER, since Cognito isn't configured). No-op
    // in production builds, so live behaviour (signed-in → /dashboard) is
    // unchanged.
    if (
      process.env.NODE_ENV !== "production" &&
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).has("preview")
    ) {
      return;
    }
    router.replace("/dashboard");
  }, [user, isLoading, needsOnboarding, router]);

  return null;
}
